import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMins(secs) {
  if (secs == null) return "—";
  const m = Math.round(secs / 60);
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function fmtShortDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function recoveryStyle(score) {
  if (score == null) return { bg: "bg-gray-50", text: "text-gray-400", stroke: "#9ca3af" };
  if (score >= 67) return { bg: "bg-green-50", text: "text-green-600", stroke: "#16a34a" };
  if (score >= 34) return { bg: "bg-yellow-50", text: "text-yellow-600", stroke: "#ca8a04" };
  return { bg: "bg-red-50", text: "text-red-600", stroke: "#dc2626" };
}

// ── Sparkline ─────────────────────────────────────────────────────────────────

function Sparkline({ values, stroke = "#6366f1", w = 80, h = 28 }) {
  const clean = (values ?? []).filter((v) => v != null);
  if (clean.length < 2) return <div style={{ width: w, height: h }} />;
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const range = max - min || 1;
  const pts = clean
    .map((v, i) => `${(i / (clean.length - 1)) * w},${h - ((v - min) / range) * (h - 2) + 1}`)
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible flex-shrink-0">
      <polyline
        points={pts}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Metric Card ───────────────────────────────────────────────────────────────

function MetricCard({ label, value, unit, note, sparkValues, stroke }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 flex flex-col gap-3">
      <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400 font-medium">{label}</p>
      <div className="flex items-end justify-between gap-2">
        <div>
          {value != null ? (
            <p className="text-2xl font-bold text-black leading-tight">
              {value}
              {unit && (
                <span className="text-sm font-normal text-gray-400 ml-1">{unit}</span>
              )}
            </p>
          ) : (
            <p className="text-2xl font-bold text-gray-200">—</p>
          )}
          {note && <p className="text-[10px] text-gray-300 mt-0.5">{note}</p>}
        </div>
        {sparkValues && (
          <Sparkline values={sparkValues} stroke={stroke || "#6366f1"} />
        )}
      </div>
    </div>
  );
}

// ── Sleep Stage Bar ───────────────────────────────────────────────────────────

function SleepBar({ deep, rem, light, awake, total }) {
  const pct = (s) => Math.max(0, ((s ?? 0) / total) * 100).toFixed(1);
  return (
    <div className="flex w-full h-3 rounded-full overflow-hidden gap-px">
      <div style={{ width: `${pct(deep)}%` }} className="bg-indigo-600" />
      <div style={{ width: `${pct(rem)}%` }} className="bg-blue-400" />
      <div style={{ width: `${pct(light)}%` }} className="bg-blue-200" />
      <div style={{ width: `${pct(awake)}%` }} className="bg-gray-200" />
    </div>
  );
}

// ── Main Export ───────────────────────────────────────────────────────────────

export default function Wearable() {
  const [metrics, setMetrics] = useState([]);
  const [sleeps, setSleeps] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [journal, setJournal] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) { navigate("/login"); return; }

      const [m, s, w, j] = await Promise.all([
        supabase
          .from("daily_metrics")
          .select("*")
          .order("metric_date", { ascending: false })
          .limit(24),
        supabase
          .from("sleep_records")
          .select("*")
          .order("start_at", { ascending: false })
          .limit(10),
        supabase
          .from("workout_records")
          .select("*")
          .order("start_at", { ascending: false })
          .limit(10),
        supabase
          .from("journal_entries")
          .select("*")
          .order("entry_date", { ascending: false })
          .limit(14),
      ]);

      setMetrics([...(m.data ?? [])].reverse()); // oldest → newest for sparklines
      setSleeps(s.data ?? []);
      setWorkouts(w.data ?? []);
      setJournal(j.data ?? []);
      setLoading(false);
    }
    load();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-400 animate-pulse">Loading wearable data…</p>
      </div>
    );
  }

  const latest = metrics.at(-1) ?? null;
  const recStyle = recoveryStyle(latest?.recovery_score);
  const latestSleep = sleeps[0] ?? null;
  const spark = (key) => metrics.map((m) => m[key]);

  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="max-w-4xl mx-auto px-6 py-12">

        {/* Breadcrumb */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-black transition-colors mb-6"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-3 w-3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Dashboard
        </Link>

        <p className="text-[10px] tracking-[0.22em] uppercase text-gray-400 font-medium mb-3">
          Dashboard · Wearable
        </p>
        <div className="flex items-end justify-between mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-black">Wearable</h1>
          <span className="text-[10px] tracking-widest uppercase text-gray-300 font-medium pb-1">
            WHOOP
          </span>
        </div>

        {/* ── Recovery ─────────────────────────────────────────────── */}
        <section className="mb-8">
          <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">
            Recovery
          </h2>
          <div className={`${recStyle.bg} rounded-2xl p-6 flex items-center justify-between gap-6`}>
            <div>
              {latest?.recovery_score != null ? (
                <>
                  <p className={`text-6xl font-bold ${recStyle.text} leading-none`}>
                    {latest.recovery_score}
                    <span className="text-2xl font-normal ml-1">%</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    {latest.recovery_score >= 67
                      ? "Well recovered — push today"
                      : latest.recovery_score >= 34
                      ? "In the green — moderate effort recommended"
                      : "Red zone — prioritize rest and recovery"}
                  </p>
                </>
              ) : (
                <p className="text-2xl font-bold text-gray-300">No data yet</p>
              )}
            </div>
            <div className="hidden sm:flex flex-col items-end gap-1">
              <Sparkline
                values={spark("recovery_score")}
                stroke={recStyle.stroke}
                w={140}
                h={52}
              />
              <p className="text-[9px] text-gray-300 tracking-wider uppercase">24-day trend</p>
            </div>
          </div>
        </section>

        {/* ── Key Metrics ───────────────────────────────────────────── */}
        <section className="mb-8">
          <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">
            Key Metrics
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <MetricCard
              label="Resting Heart Rate"
              value={latest?.resting_heart_rate}
              unit="bpm"
              sparkValues={spark("resting_heart_rate")}
              stroke="#ef4444"
            />
            <MetricCard
              label="Heart Rate Variability"
              value={latest?.hrv_rmssd_ms}
              unit="ms"
              sparkValues={spark("hrv_rmssd_ms")}
              stroke="#6366f1"
            />
            <MetricCard
              label="Blood Oxygen (SpO₂)"
              value={latest?.spo2_pct}
              unit="%"
              sparkValues={spark("spo2_pct")}
              stroke="#0ea5e9"
            />
            <MetricCard
              label="Respiratory Rate"
              value={latest?.respiratory_rate}
              unit="rpm"
              sparkValues={spark("respiratory_rate")}
              stroke="#8b5cf6"
            />
            <MetricCard
              label="Skin Temperature"
              value={latest?.skin_temp_celsius}
              unit="°C"
              sparkValues={spark("skin_temp_celsius")}
              stroke="#f59e0b"
            />
            <MetricCard
              label="Day Strain"
              value={latest?.day_strain}
              note="scale 0 – 21"
              sparkValues={spark("day_strain")}
              stroke="#f97316"
            />
            <MetricCard
              label="Calories Burned"
              value={latest?.calories_kcal}
              unit="kcal"
              sparkValues={spark("calories_kcal")}
              stroke="#10b981"
            />
            <MetricCard
              label="VO₂ Max"
              note="not tracked by current device"
            />
          </div>
        </section>

        {/* ── Sleep ────────────────────────────────────────────────── */}
        {latestSleep && (
          <section className="mb-8">
            <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">
              Last Sleep
              <span className="ml-2 normal-case tracking-normal font-normal text-gray-300">
                {fmtShortDate(latestSleep.start_at)}
              </span>
            </h2>
            <div className="bg-white border border-gray-100 rounded-2xl p-6">
              {/* Top stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 mb-1">Asleep</p>
                  <p className="text-2xl font-bold text-black">
                    {fmtMins(latestSleep.duration_seconds)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 mb-1">Performance</p>
                  <p className="text-2xl font-bold text-black">
                    {latestSleep.sleep_performance}
                    <span className="text-sm font-normal text-gray-400 ml-0.5">%</span>
                  </p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 mb-1">Efficiency</p>
                  <p className="text-2xl font-bold text-black">
                    {latestSleep.sleep_efficiency}
                    <span className="text-sm font-normal text-gray-400 ml-0.5">%</span>
                  </p>
                </div>
              </div>

              {/* Stage bar */}
              {latestSleep.in_bed_seconds && (
                <div className="mb-5">
                  <SleepBar
                    deep={latestSleep.slow_wave_seconds}
                    rem={latestSleep.rem_sleep_seconds}
                    light={latestSleep.light_sleep_seconds}
                    awake={latestSleep.awake_seconds}
                    total={latestSleep.in_bed_seconds}
                  />
                </div>
              )}

              {/* Stage legend */}
              <div className="grid grid-cols-4 gap-2 mb-5">
                {[
                  { label: "Deep (SWS)", secs: latestSleep.slow_wave_seconds, color: "bg-indigo-600" },
                  { label: "REM", secs: latestSleep.rem_sleep_seconds, color: "bg-blue-400" },
                  { label: "Light", secs: latestSleep.light_sleep_seconds, color: "bg-blue-200" },
                  { label: "Awake", secs: latestSleep.awake_seconds, color: "bg-gray-200" },
                ].map(({ label, secs, color }) => (
                  <div key={label} className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${color}`} />
                    <div>
                      <p className="text-[10px] text-gray-400">{label}</p>
                      <p className="text-sm font-semibold text-black">{fmtMins(secs)}</p>
                      {latestSleep.in_bed_seconds && (
                        <p className="text-[10px] text-gray-300">
                          {(((secs ?? 0) / latestSleep.in_bed_seconds) * 100).toFixed(0)}%
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Sleep need / debt / resp */}
              <div className="border-t border-gray-50 pt-4 flex flex-wrap gap-6">
                {latestSleep.sleep_need_seconds != null && (
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 mb-1">Sleep Need</p>
                    <p className="text-sm font-semibold text-black">
                      {fmtMins(latestSleep.sleep_need_seconds)}
                    </p>
                  </div>
                )}
                {latestSleep.sleep_debt_seconds != null && (
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 mb-1">Sleep Debt</p>
                    <p className={`text-sm font-semibold ${latestSleep.sleep_debt_seconds > 0 ? "text-orange-500" : "text-green-600"}`}>
                      {fmtMins(latestSleep.sleep_debt_seconds)}
                    </p>
                  </div>
                )}
                {latestSleep.respiratory_rate != null && (
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 mb-1">Resp Rate</p>
                    <p className="text-sm font-semibold text-black">
                      {latestSleep.respiratory_rate} rpm
                    </p>
                  </div>
                )}
                {latestSleep.sleep_consistency != null && (
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 mb-1">Consistency</p>
                    <p className="text-sm font-semibold text-black">
                      {latestSleep.sleep_consistency}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ── Recent Workouts ───────────────────────────────────────── */}
        {workouts.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">
              Recent Workouts
            </h2>
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    {["Date", "Activity", "Duration", "Strain", "Avg HR", "Max HR"].map((h) => (
                      <th
                        key={h}
                        className="text-left text-[10px] tracking-[0.15em] uppercase text-gray-400 font-medium py-3 px-4 first:pl-5 last:pr-5"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {workouts.map((w) => {
                    const durMin = Math.round(
                      (new Date(w.end_at) - new Date(w.start_at)) / 60000
                    );
                    return (
                      <tr
                        key={w.id}
                        className="border-t border-gray-50 hover:bg-gray-50 transition-colors text-sm"
                      >
                        <td className="py-3 pl-5 pr-4 text-gray-500 whitespace-nowrap">
                          {fmtShortDate(w.start_at)}
                        </td>
                        <td className="py-3 pr-4 font-medium text-black">{w.sport_name}</td>
                        <td className="py-3 pr-4 tabular-nums text-gray-700">{durMin}m</td>
                        <td className="py-3 pr-4 tabular-nums text-gray-700">
                          {w.strain?.toFixed(1)}
                        </td>
                        <td className="py-3 pr-4 tabular-nums text-gray-700">
                          {w.avg_heart_rate}
                        </td>
                        <td className="py-3 pr-5 tabular-nums text-gray-700">
                          {w.max_heart_rate}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── Lifestyle Factors ─────────────────────────────────────── */}
        {journal.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">
              Lifestyle Factors
            </h2>
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    {["Date", "Alcohol", "Caffeine", "Food Late", "Read in Bed"].map((h) => (
                      <th
                        key={h}
                        className="text-left text-[10px] tracking-[0.15em] uppercase text-gray-400 font-medium py-3 px-4 first:pl-5 last:pr-5"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {journal.map((j) => {
                    const dot = (val) =>
                      val ? (
                        <span className="inline-flex items-center gap-1 text-orange-500 text-xs font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      );
                    return (
                      <tr
                        key={j.id}
                        className="border-t border-gray-50 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-3 pl-5 pr-4 text-sm text-gray-500 whitespace-nowrap">
                          {new Date(j.entry_date + "T12:00:00").toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                        <td className="py-3 pr-4">{dot(j.had_alcohol)}</td>
                        <td className="py-3 pr-4">{dot(j.had_caffeine)}</td>
                        <td className="py-3 pr-4">{dot(j.ate_before_bed)}</td>
                        <td className="py-3 pr-5">{dot(j.read_in_bed)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

      </div>

      <p className="text-[10px] text-gray-300 text-center pb-2">
        © {year} Dylan Zajac. All rights reserved.
      </p>
    </div>
  );
}
